import { Component, computed, effect, inject, signal } from '@angular/core'
import { MDMABestAvailable, MDMAJobStatus } from '../../settlements/mdma-settlements.types'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import { DateRangeFilterPickerComponent } from '../../date-range-filter-picker/date-range-filter-picker.component'
import { EntitySelectorComponent } from '../../entity-selector/entity-selector.component'
import { EntityRef, EntityRefWithUID } from 'src/app/shared/models/referenceData'
import { MDMASettlementsService } from '../../settlements/mdma-settlements.service'
import { DateDisplayComponent } from '../../date-display/date-display.component'
import { dateOnlyFormat } from 'src/app/shared/utilities/date'
import { LabeledDisplayComponent } from '../../labeled-display/labeled-display.component'
import { ToastService } from '../../toasts/toasts.service'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    EntitySelectorComponent,
    DateDisplayComponent,
    LabeledDisplayComponent,
  ],
  styles: `
        .error {
            color: red;
            align-self: flex-end;
        }
        .static-data {
            display: block;
            height: 2rem;
            border-bottom: 1px solid transparent;
            padding: 1rem 0 0.25rem 0.25rem;
            font-size: 0.9rem;
            line-height: 1.25rem;
        }
        .section-header {
            margin: 0.75rem 0 -1rem 0;
            padding: 0;
            text-align: center;
            color: black;
        }

        div[app-modal-footer] {
            margin-top: 1rem;
        }
    `,
  template: `
    @if(selectedRun()) {
        <div app-modal-header>Select Comparison Run Values</div>

        <div app-modal-body>
            <h3 class="section-header">Base Settlement</h3>
            <div app-labeled-property label="Operating Day">
              <div app-date-display [value]="selectedRun()?.operating_date" [format]="dateOnlyFormat"></div>
            </div>
            <div app-labeled-property label="Settlement Type">
              <div app-labeled-display [value]="selectedRun()?.settlement_type_code"></div>
            </div>
            <h3 class="section-header">Comparison Settlement</h3>
            <div app-labeled-property label="Operating Day">
              <div dateRangeFilterPicker
                  [value]="compOperatingDate() ?? undefined"
                  (valueChange)="handleOperatingDate($event)"
                  [showOperation]="false"
                  [showRange]="false"
                  [showTime]="false"
                  [showSeconds]="false"
              ></div>
            </div>
            <div app-labeled-property label="Settlement Type">
              <div
                app-entity-selector
                [slim]="true"
                label="Settlement Type"
                [config]="{
                    resource: 'mdmaSettlementTypes', 
                    allowMultiple: false
                }"
                [value]="compSettlementType()"
                (valueChange)="handleSettlementType($event)"
              ></div>
            </div>
            @if(!loading() && error()) {
                <span class="error">{{error()}}</span>
            }
        </div>

        <div app-modal-footer>
            <button
                class="action-btn primary"
                [disabled]="!canSubmit()"
                (click)="onSelect()(selectedRun()!, comparison()!)"
            >
                View Comparison Report
            </button>
            <button class="action-btn" (click)="onCancel()()">Cancel</button>
        </div>
    }`,
})
export class MDMAComparisonSelectionModalComponent {
  private readonly settlementsService = inject(MDMASettlementsService)
  private readonly toastService = inject(ToastService)

  protected readonly dateOnlyFormat = dateOnlyFormat

  protected readonly loading = signal(false)
  protected readonly error = signal<string | null>(null)
  protected readonly compSettlementType = signal<EntityRefWithUID | null>(null)
  protected readonly compOperatingDate = signal<DateRangeFilterStateValue | null>(null)
  protected readonly comparison = signal<MDMABestAvailable | null>(null)
  protected readonly selectedRun = signal<MDMAJobStatus | undefined>(undefined)
  protected readonly onSelect = signal<(a: MDMAJobStatus, b: MDMABestAvailable) => void>(() => void 0)
  protected readonly onCancel = signal<() => void>(() => void 0)

  protected readonly canSubmit = computed(() => {
    return !this.loading() && !this.error() && this.selectedRun() && this.comparison()
  })

  constructor() {
    effect(() => {
      const operatingDate = this.compOperatingDate()
      const settlementType = this.compSettlementType()
      if(operatingDate?.value && settlementType) {
        this.loading.set(true)
        this.error.set(null)
        this.settlementsService
          .bestAvailableMDMA(operatingDate.value as Date, settlementType.entity_uid)
          .subscribe({
            error: e => {
              if (e.status === 404) {
                this.error.set('No settlement found matching your request')
              } else {
                this.error.set('An error occurred while searching for a settlement')
                this.toastService.showError(e)
              }
              this.comparison.set(null)
              this.loading.set(false)
            },
            next: comp => {
              this.comparison.set(comp)
              this.error.set(null)
              this.loading.set(false)
            },
          })
      }
    })
  }

  init(overrides: Partial<{
    selectedRun: MDMAJobStatus
    onSelect: (a: MDMAJobStatus, b: MDMABestAvailable) => void,
    onCancel: () => void
  }>) {
    this.selectedRun.update(v => overrides.selectedRun ?? v)
    this.onSelect.update(v => overrides.onSelect ?? v)
    this.onCancel.update(v => overrides.onCancel ?? v)
  }

  handleOperatingDate(evt: DateRangeFilterStateValue) {
    this.compOperatingDate.set(evt)
  }
  handleSettlementType(evt: EntityRef) {
    this.compSettlementType.set(evt as EntityRefWithUID)
  }
}